import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Edit3, Copy, Trash2, Code2, Mail, FileText, Copy as CopyIcon,
    CheckCheck,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { findTokens, substituteTokens, wrapEmailHtml, renderPreviewHtml, renderPreviewSubject, buildEmailLogo, previewLayoutStyles } from "@/lib/tokens";
import { copyRichHtml, copyPlainText } from "@/lib/clipboard";
import { sanitizeBodyHtml, sanitizeSubjectText } from "@/lib/sanitize";
import { BrandMark, Avatar } from "@/components/BrandMark";
import { relativeTime } from "@/lib/time";

const INCLUDE_LOGO_KEY = "esm_include_brand_logo";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { brandByKey, reloadTemplates, resolveStyleForTemplate } = useData();

    const [template, setTemplate] = useState(null);
    const [values, setValues] = useState({});
    const [showSource, setShowSource] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState("");
    const [includeLogo, setIncludeLogo] = useState(() => {
        try {
            return localStorage.getItem(INCLUDE_LOGO_KEY) === "true";
        } catch (_e) {
            return false;
        }
    });

    const toggleIncludeLogo = () => {
        setIncludeLogo((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(INCLUDE_LOGO_KEY, next ? "true" : "false");
            } catch (_e) {
                // ignore storage failures
            }
            return next;
        });
    };

    useEffect(() => {
        setError("");
        api.get(`/templates/${id}`).then(
            (r) => setTemplate(r.data),
            (e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
        );
    }, [id]);

    const tokens = useMemo(() => {
        if (!template) return [];
        return findTokens(template.subject, template.body);
    }, [template]);

    const canEdit = template && (user.access_level === "admin" || template.owner_id === user.id);
    const brand = template ? brandByKey(template.brand_key) : null;

    if (error) {
        return (
            <div className="max-w-3xl mx-auto p-8">
                <Link to="/" className="btn-ghost mb-6"><ArrowLeft size={14}/> Back to library</Link>
                <div className="tms-card p-8" data-testid="detail-error">
                    <div style={{ color: "var(--tms-danger)", fontWeight: 600 }}>{error}</div>
                </div>
            </div>
        );
    }

    if (!template) {
        return <div className="p-8" style={{ color: "var(--tms-muted)" }} data-testid="detail-loading">Loading template…</div>;
    }

    const rawBodyHtml = sanitizeBodyHtml(substituteTokens(template.body, values));       // for copy
    const rawSubject = sanitizeSubjectText(substituteTokens(template.subject, values));
    const hasBrandLogo = !!brand?.active_logo;
    const emailLogo = includeLogo && hasBrandLogo ? buildEmailLogo(brand, BACKEND_URL) : null;
    const effectiveStyle = resolveStyleForTemplate(template);
    const wrappedRawHtml = wrapEmailHtml(rawBodyHtml, { logo: emailLogo, style: effectiveStyle });
    const previewStyles = previewLayoutStyles(effectiveStyle);

    // Preview strings are ALSO sanitised before dangerouslySetInnerHTML —
    // renderPreviewHtml re-wraps unfilled tokens in <span class="token-empty">
    // and filled ones in <span class="token-filled">, which we allow.
    const previewBodyHtml = sanitizeBodyHtml(renderPreviewHtml(template.body, values));
    const previewSubject = sanitizeBodyHtml(renderPreviewSubject(template.subject, values));

    const onCopyRich = async () => {
        const ok = await copyRichHtml(wrappedRawHtml);
        toast[ok ? "success" : "error"](ok ? "Formatted email copied — paste into Outlook" : "Copy failed", { position: "bottom-center" });
    };
    const onCopyHtml = async () => {
        const ok = await copyPlainText(wrappedRawHtml);
        toast[ok ? "success" : "error"](ok ? "HTML source copied" : "Copy failed", { position: "bottom-center" });
    };
    const onCopySubject = async () => {
        const ok = await copyPlainText(rawSubject);
        toast[ok ? "success" : "error"](ok ? "Subject line copied" : "Copy failed", { position: "bottom-center" });
    };

    const onDuplicate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/templates/${template.id}/duplicate`);
            toast.success("Template duplicated", { position: "bottom-center" });
            await reloadTemplates();
            nav(`/templates/${data.id}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Duplicate failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const onDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/templates/${template.id}`);
            toast.success("Template deleted", { position: "bottom-center" });
            await reloadTemplates();
            nav("/");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    return (
        <div className="max-w-[1280px] mx-auto px-6 py-6" data-testid="template-detail-page">
            <div className="flex items-center justify-between mb-4">
                <Link to="/" className="btn-ghost" data-testid="back-to-library-link">
                    <ArrowLeft size={14} /> Back to library
                </Link>
                <div className="flex gap-2 items-center">
                    {canEdit && (
                        <button className="btn-outline" onClick={() => nav(`/templates/${id}/edit`)} data-testid="edit-template-btn">
                            <Edit3 size={14} /> Edit
                        </button>
                    )}
                    <button className="btn-outline" onClick={onDuplicate} disabled={busy} data-testid="duplicate-template-btn">
                        <Copy size={14} /> Duplicate
                    </button>
                    {canEdit && !confirmDelete && (
                        <button className="btn-danger" onClick={() => setConfirmDelete(true)} data-testid="delete-template-btn">
                            <Trash2 size={14} /> Delete
                        </button>
                    )}
                    {canEdit && confirmDelete && (
                        <div className="flex gap-2 items-center" data-testid="delete-confirm">
                            <span style={{ fontSize: 12, color: "var(--tms-danger)", fontWeight: 500 }}>Really delete?</span>
                            <button className="btn-outline" onClick={() => setConfirmDelete(false)} data-testid="delete-cancel-btn">Cancel</button>
                            <button className="btn-danger" onClick={onDelete} disabled={busy} data-testid="delete-confirm-btn">
                                Yes, delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Title block */}
            <div className="tms-card p-6 mb-6 fade-in">
                <div className="flex items-start gap-4">
                    <BrandMark brand={brand} code={brand?.short_code || template.brand_key.toUpperCase()} size="lg" />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span
                                className="tms-chip"
                                style={{ background: "var(--tms-navy)", color: "#fff", height: 26, padding: "0 12px" }}
                                data-testid="detail-category-pill"
                            >
                                {template.category}
                            </span>
                            <span style={{ fontSize: 12, color: "var(--tms-muted)" }}>{brand?.label || template.brand_key}</span>
                            {template.draft && <span className="tms-chip status-draft">Draft</span>}
                            {template.shared && !template.draft && <span className="tms-chip status-shared">Shared with team</span>}
                            {!template.shared && !template.draft && <span className="tms-chip status-private">Private</span>}
                        </div>
                        <h1 style={{ fontSize: 26, fontWeight: 600, color: "var(--tms-navy)", lineHeight: 1.2 }} data-testid="detail-template-name">
                            {template.name}
                        </h1>
                        <div className="flex items-center gap-2 mt-2">
                            <Avatar initials={template.owner?.initials} name={template.owner?.name} size="sm" />
                            <span style={{ fontSize: 13, color: "var(--tms-muted)" }}>
                                {template.owner_id === user.id ? "You" : template.owner?.name} · updated {relativeTime(template.updated_at)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {(template.description || template.usage_scenario) && (
                <div
                    className="tms-card p-5 mb-6"
                    data-testid="detail-about-block"
                    style={{ background: "#F8FAFC", borderColor: "var(--tms-border)" }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "var(--tms-muted)",
                            marginBottom: 10,
                        }}
                    >
                        About this template
                    </div>
                    {template.description && (
                        <div style={{ marginBottom: template.usage_scenario ? 12 : 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 3 }}>
                                Description
                            </div>
                            <div
                                data-testid="detail-description"
                                style={{ fontSize: 13, color: "var(--tms-text)", lineHeight: 1.55 }}
                            >
                                {template.description}
                            </div>
                        </div>
                    )}
                    {template.usage_scenario && (
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 3 }}>
                                When to use
                            </div>
                            <div
                                data-testid="detail-usage-scenario"
                                style={{ fontSize: 13, color: "var(--tms-text)", lineHeight: 1.55 }}
                            >
                                {template.usage_scenario}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
                {/* Preview column */}
                <div>
                    <div className="tms-card p-5 mb-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Mail size={14} style={{ color: "var(--tms-navy)" }} />
                            <span style={{ fontSize: 12, color: "var(--tms-muted)", fontWeight: 500 }}>To:</span>
                            <span style={{ fontSize: 13, color: "var(--tms-navy)", fontWeight: 500 }}>your client</span>
                        </div>
                        <div className="pb-3 mb-3 border-b" style={{ borderColor: "var(--tms-border)" }}>
                            <div style={{ fontSize: 11, color: "var(--tms-muted)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                                Subject
                            </div>
                            <div
                                data-testid="preview-subject"
                                style={{ fontSize: 15, color: "var(--tms-navy)", fontWeight: 500 }}
                                dangerouslySetInnerHTML={{ __html: previewSubject }}
                            />
                        </div>
                        {/* Email body constrained to the layout's content_width */}
                        <div style={previewStyles.outer}>
                            <div
                                data-testid="preview-body"
                                style={previewStyles.inner}
                            >
                                {emailLogo && (
                                    <div
                                        data-testid="preview-brand-logo-wrap"
                                        style={{ paddingBottom: 20, textAlign: "left" }}
                                    >
                                        <img
                                            src={emailLogo.absoluteUrl}
                                            alt={emailLogo.alt}
                                            height={emailLogo.height}
                                            data-testid="preview-brand-logo"
                                            style={{
                                                height: emailLogo.height,
                                                width: "auto",
                                                maxWidth: 340,
                                                display: "block",
                                                border: 0,
                                            }}
                                        />
                                    </div>
                                )}
                                <div dangerouslySetInnerHTML={{ __html: previewBodyHtml }} />
                            </div>
                        </div>
                    </div>

                    <div className="tms-card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Code2 size={14} style={{ color: "var(--tms-navy)" }} />
                                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)" }}>HTML source</span>
                            </div>
                            <button
                                className="btn-ghost"
                                onClick={() => setShowSource((s) => !s)}
                                data-testid="toggle-html-source-btn"
                                aria-pressed={showSource}
                            >
                                {showSource ? "Hide" : "Show"}
                            </button>
                        </div>
                        {showSource && (
                            <pre className="html-source-pre" data-testid="html-source-pre">{wrappedRawHtml}</pre>
                        )}
                    </div>
                </div>

                {/* Right panel: fill fields + copy actions */}
                <aside>
                    <div className="tms-card p-5 mb-4">
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 4 }}>
                            Fill in the details
                        </div>
                        <div style={{ fontSize: 12, color: "var(--tms-muted)", marginBottom: 16 }}>
                            Typing here updates the preview live.
                        </div>
                        {tokens.length === 0 ? (
                            <div
                                data-testid="no-fields-hint"
                                style={{ background: "var(--tms-green-tint)", padding: "10px 12px", borderRadius: 10, color: "#4d7b1b", fontSize: 13 }}
                            >
                                This template has no fill-in fields — it&apos;s ready to copy as-is.
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="fill-fields-list">
                                {tokens.map((t) => (
                                    <div key={t}>
                                        <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 4 }}>
                                            {t}
                                        </label>
                                        <input
                                            className="tms-input"
                                            data-testid={`fill-field-${t.replace(/\s+/g, "-").toLowerCase()}`}
                                            value={values[t] || ""}
                                            onChange={(e) => setValues({ ...values, [t]: e.target.value })}
                                            placeholder={t}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="tms-card p-5 space-y-3">
                        {hasBrandLogo && (
                            <div
                                className="flex items-center justify-between pb-3 mb-1 border-b"
                                style={{ borderColor: "var(--tms-border)" }}
                                data-testid="include-brand-logo-row"
                            >
                                <div className="min-w-0 pr-3">
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)" }}>
                                        Include brand logo at the top
                                    </div>
                                    <div style={{ fontSize: 11, color: "var(--tms-muted)", marginTop: 2 }}>
                                        Adds the {brand?.label || "brand"} logo above the email body.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={includeLogo}
                                    data-testid="include-brand-logo-toggle"
                                    onClick={toggleIncludeLogo}
                                    className="relative rounded-full transition-colors shrink-0"
                                    style={{
                                        width: 44,
                                        height: 24,
                                        background: includeLogo ? "var(--tms-green)" : "#c9d0d9",
                                    }}
                                >
                                    <span
                                        className="absolute rounded-full bg-white transition-transform"
                                        style={{
                                            width: 18,
                                            height: 18,
                                            top: 3,
                                            left: 3,
                                            transform: includeLogo ? "translateX(20px)" : "translateX(0)",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                        }}
                                    />
                                </button>
                            </div>
                        )}
                        <button
                            onClick={onCopyRich}
                            className="btn-primary-green w-full justify-center"
                            style={{ padding: "14px 16px", fontSize: 14 }}
                            data-testid="copy-formatted-btn"
                        >
                            <CheckCheck size={16} /> Copy formatted email
                        </button>
                        <button
                            onClick={onCopyHtml}
                            className="btn-outline w-full justify-center"
                            data-testid="copy-html-btn"
                        >
                            <FileText size={14} /> Copy HTML source
                        </button>
                        <button
                            onClick={onCopySubject}
                            className="btn-outline w-full justify-center"
                            data-testid="copy-subject-btn"
                        >
                            <CopyIcon size={14} /> Copy subject line only
                        </button>
                    </div>
                </aside>
            </div>
        </div>
    );
}
